<script setup lang="ts">
import { onBeforeUnmount, watch } from 'vue'
import IconSvg from '@/components/IconSvg.vue'
import { dismissToast, useToast } from '@/composables/useToast'

const { items } = useToast()

const timers = new Map<number, ReturnType<typeof setTimeout>>()

function scheduleDismiss(id: number, duration: number): void {
  if (timers.has(id)) return
  const timer = setTimeout(() => {
    timers.delete(id)
    dismissToast(id)
  }, duration)
  timers.set(id, timer)
}

onBeforeUnmount(() => {
  timers.forEach((timer) => clearTimeout(timer))
  timers.clear()
})

watch(
  () => items.map((i) => i.id).join(','),
  () => {
    items.forEach((item) => scheduleDismiss(item.id, item.duration))
  },
  { immediate: true, flush: 'post' },
)
</script>

<template>
  <Teleport to="body">
    <div
      class="app-toast-container"
      aria-live="polite"
    >
      <TransitionGroup name="app-toast">
        <div
          v-for="item in items"
          :key="item.id"
          class="app-toast"
          role="status"
        >
          <IconSvg
            name="info"
            :size="16"
          />
          <span>{{ item.message }}</span>
        </div>
      </TransitionGroup>
    </div>
  </Teleport>
</template>

<style scoped>
.app-toast-container {
  position: fixed;
  top: calc(var(--nav-height, 64px) + 16px);
  left: 50%;
  transform: translateX(-50%);
  z-index: 300;
  display: flex;
  flex-direction: column;
  gap: 12px;
  pointer-events: none;
}

.app-toast {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 22px;
  background: var(--ink-900);
  color: #fff;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 500;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.3);
  pointer-events: auto;
}

.app-toast-enter-active,
.app-toast-leave-active {
  transition:
    opacity 0.25s ease,
    transform 0.25s ease;
}

.app-toast-enter-from {
  opacity: 0;
  transform: translateY(8px);
}

.app-toast-leave-to {
  opacity: 0;
  transform: translateY(8px);
}
</style>
